import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { sanitizeText } from "@/lib/sanitize";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalTextarea } from "@/components/ui/BrutalTextarea";
import { BrutalRadioGroup, BrutalRadioGroupItem } from "@/components/ui/BrutalRadioGroup";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileId?: string;
  companyId?: string;
  eventId?: string;
  jobId?: string;
  targetName: string;
}

const REPORT_REASONS = [
  { value: "hate_speech", labelKey: "reports.reasons.hateSpeech" },
  { value: "discrimination", labelKey: "reports.reasons.discrimination" },
  { value: "fraud", labelKey: "reports.reasons.fraud" },
  { value: "inappropriate_content", labelKey: "reports.reasons.inappropriateContent" },
  { value: "fake_profile", labelKey: "reports.reasons.fakeProfile" },
  { value: "other", labelKey: "reports.reasons.other" },
];

export function ReportModal({
  isOpen,
  onClose,
  profileId,
  companyId,
  eventId,
  jobId,
  targetName,
}: ReportModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!reason) {
      toast({
        title: t("common.error"),
        description: t("reports.selectReason"),
        variant: "error",
      });
      return;
    }

    if (!user) {
      toast({
        title: t("common.error"),
        description: t("auth.loginRequired"),
        variant: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke("create-user-report", {
        body: {
          profileId,
          companyId,
          eventId,
          jobId,
          reason,
          details: details.trim() ? sanitizeText(details.trim()) : null,
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      toast({
        title: t("reports.success"),
        description: t("reports.successDesc"),
      });

      setReason("");
      setDetails("");
      onClose();
    } catch (error: any) {
      console.error("Error submitting report:", error);
      toast({
        title: t("common.error"),
        description: error.message || t("reports.errorSubmitting"),
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md border-[3px] border-dark shadow-[8px_8px_0px_0px] shadow-dark">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            {t("reports.title")}
          </DialogTitle>
          <DialogDescription>
            {t("reports.description", { name: targetName })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          <div>
            <label className="font-heading text-sm font-bold uppercase tracking-wide mb-3 block">
              {t("reports.reason")}
            </label>
            <BrutalRadioGroup value={reason} onValueChange={setReason}>
              {REPORT_REASONS.map((r) => (
                <div key={r.value} className="flex items-center space-x-3">
                  <BrutalRadioGroupItem value={r.value} id={r.value} />
                  <Label htmlFor={r.value} className="cursor-pointer">
                    {t(r.labelKey)}
                  </Label>
                </div>
              ))}
            </BrutalRadioGroup>
          </div>

          <div>
            <label className="font-heading text-sm font-bold uppercase tracking-wide mb-2 block">
              {t("reports.details")} ({t("common.optional")})
            </label>
            <BrutalTextarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={t("reports.detailsPlaceholder")}
              rows={4}
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {details.length}/1000
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <BrutalButton
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={loading}
            >
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton
              variant="danger"
              onClick={handleSubmit}
              className="flex-1"
              disabled={loading || !reason}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {t("reports.submit")}
            </BrutalButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
